import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { Card, CardBody, CardHeader } from 'reactstrap';
import SpringLoader from '../../../../../components/SpringLoader/SpringLoader.js';
import AlertService from '../../../../../components/SweetAlert';
import ToastUtil from '../../../../../components/ToastNotification/ToastUtil';
import { config } from '../../../../../config/index';
import { GET, POST, newFetch } from '../../../../../helpers/fetch';
import UserManagement from './ClientUsers';
import AddUserManagement from './ClientUsers/AddClientUser';
import EditUserManagement from './ClientUsers/EditClientUser';
const { API_URL } = config;

const ClientDetail = ({ history, match: { params } }) => {
  const client_id = params.client_id;
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('sites');

  const [client, setClient] = useState({});
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeUserTabPage, setActiveUserTabPage] = useState('list');
  const [selectedUser, setSelectedUser] = useState('');
  const [leaveApprovers, setLeaveApprovers] = useState([]);
  const [validated, setValidated] = useState(false);

  // New state for Archive and Carry Forward
  const [archiveYear, setArchiveYear] = useState('');
  const [carryForwardYear, setCarryForwardYear] = useState('');
  const [processingArchive, setProcessingArchive] = useState(false);
  const [processingCarryForward, setProcessingCarryForward] = useState(false);

  const fetchClient = async () => {
    const url = `${API_URL}/client/${client_id}`;

    if (token) {
      let response = await fetch(url, {
        mode: 'cors',
        method: 'GET',
        withCredentials: true,
        headers: {
          Authorization: token,
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json; charset=utf-8',
        },
      });

      if (response.ok) {
        let data = await response.json();
        setClient(data.data);
      } else {
        console.log(response.message);
      }
    } else {
      console.log('Token is missing');
    }
  };

  const fetchSites = async () => {
    const url = `${API_URL}/client/${client_id}/site`;

    if (token) {
      let response = await fetch(url, {
        mode: 'cors',
        method: 'GET',
        withCredentials: true,
        headers: {
          Authorization: token,
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json; charset=utf-8',
        },
      });

      if (response.ok) {
        let data = await response.json();
        setSites(data.data);
      } else {
        console.log(response.message);
      }
    } else {
      console.log('Token is missing');
    }
  };

  const handleSubmitLeaveSettingsHandler = async (event) => {
    event.preventDefault();

    await handleSubmitLeaveApprover();
  };

  const fetchLeaveApprovers = async () => {
    const fetch_path = `/client/${client_id}/leaveapprovers`;

    if (!token) {
      console.log('Token is missing');
      return;
    }

    try {
      const response = await newFetch(GET, fetch_path);

      if (response.success) {
        const approvers = response.data.map((item, index) => ({
          id: `${Date.now()}_${index}`,
          value: item.type,
        }));

        setLeaveApprovers(approvers);
      } else {
        console.log('Error:', response.message);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const handleSubmitLeaveApprover = async () => {
    if (!validateApprovers()) {
      return;
    }

    try {
      let submit_site_path = `/client/${client_id}/create-update-leaveapprover`;
      const response = await newFetch(POST, submit_site_path, leaveApprovers);

      if (response.success) {
        ToastUtil.success(`Leave approvers updated successfully.`);
      } else {
        ToastUtil.error(
          `Failed to update leave approvers: ${response.message}`,
        );
      }
    } catch (error) {
      console.error('Error in handleSubmitLeaveApprover:', error);
      ToastUtil.error('An unexpected error occurred. Please try again.');
    }
  };

  const validateApprovers = () => {
    const errors = [];

    leaveApprovers.forEach((approver, index) => {
      if (!approver.value) {
        errors.push(
          `For Approver ${index + 1}: Please select an approver.\n\n`,
        );
      }
    });

    if (errors.length > 0) {
      const errorMessage = `Error:\n\n${errors.join('\n')}`;
      ToastUtil.error(errorMessage);
      return false;
    }

    return true;
  };

  const addLeaveApprover = (e) => {
    e.preventDefault();
    setLeaveApprovers([...leaveApprovers, { id: Date.now(), value: '' }]);
  };

  const removeLeaveApprover = (id) => {
    setLeaveApprovers(leaveApprovers.filter((approver) => approver.id !== id));
  };

  const handleLeaveApproverChange = (id, value) => {
    setLeaveApprovers(
      leaveApprovers.map((approver) =>
        approver.id === id ? { ...approver, value } : approver,
      ),
    );
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 1; i <= 1; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  const handleArchiveLeaves = async () => {
    if (!archiveYear) {
      ToastUtil.error('Please select a year to archive.');
      return;
    }

    // Confirm action with AlertService
    const isConfirmed = await AlertService.confirm(
      '',
      `Are you sure you want to archive leaves for year ${archiveYear}?`,
    );

    if (!isConfirmed) return; // Stop if user cancels

    setProcessingArchive(true);
    try {
      const api_path = `/client/${client_id}/archive-leaves`;
      const response = await newFetch(POST, api_path, { year: archiveYear });

      if (response.success) {
        ToastUtil.success(`Leaves for ${archiveYear} archived successfully.`);
      } else {
        ToastUtil.error(
          `Failed to archive leaves: ${response.message || 'Unknown error'}`,
        );
      }
    } catch (error) {
      console.error('Error in handleArchiveLeaves:', error);
      ToastUtil.error('An unexpected error occurred. Please try again.');
    } finally {
      setProcessingArchive(false);
    }
  };

  const handleCarryForwardLeaves = async () => {
    if (!carryForwardYear) {
      ToastUtil.error('Please select a year to carry forward.');
      return;
    }

    // Confirm action with AlertService
    const isConfirmed = await AlertService.confirm(
      '',
      `Are you sure you want to carry forward leaves from year ${carryForwardYear}?`,
    );

    if (!isConfirmed) return; // Stop if user cancels

    setProcessingCarryForward(true);
    try {
      const api_path = `/client/${client_id}/carry-forward-leaves`;
      const response = await newFetch(POST, api_path, {
        year: carryForwardYear,
      });

      if (response.success) {
        ToastUtil.success(
          `Leaves from ${carryForwardYear} carried forward successfully.`,
        );
      } else {
        ToastUtil.error(
          `Failed to carry forward leaves: ${
            response.message || 'Unknown error'
          }`,
        );
      }
    } catch (error) {
      console.error('Error in handleCarryForwardLeaves:', error);
      ToastUtil.error('An unexpected error occurred. Please try again.');
    } finally {
      setProcessingCarryForward(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchClient(), fetchLeaveApprovers(), fetchSites()]).finally(
      () => setLoading(false),
    );

    const previousYear = new Date().getFullYear() - 1;
    setArchiveYear(previousYear.toString());
    setCarryForwardYear(previousYear.toString());
  }, []);

  return (
    <div>
      <div
        className="d-flex bg-white justify-content-between pt-4 px-5 pb-4"
        style={{ background: '#3c3c3c' }}
      >
        <div className="d-flex flex-column">
          <NavLink
            to="/clients"
            className="text-decoration-none"
            style={{ color: 'inherit' }}
          >
            <div style={{ color: '#0d6efd' }}>
              <i className="fas fa-chevron-left pe-2"></i>Back to Client
              Management
            </div>
          </NavLink>
          {!loading && (
            <div className="d-flex align-items-center justify-content-center pt-4">
              <i className="fas fa-warehouse fa-2x fa-fw"></i>
              <h3 className="pt-2 ps-3">{client.name}</h3>
            </div>
          )}
        </div>
        <div className="mt-auto">
          <button
            onClick={() => history.push(`/clients/client/${client.id}/edit`)}
            className="btn btn-outline-brand rounded adjust-fs adjust-shadow"
            style={{ width: '200px' }}
          >
            Edit Client
          </button>
        </div>
      </div>
      {loading ? (
        <div className="springloader-holder">
          <SpringLoader></SpringLoader>
        </div>
      ) : (
        <div className="d-flex align-items-start">
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ margin: '3rem' }}
          >
            <div
              className="d-flex justify-content-start"
              style={{ minWidth: 0, maxWidth: '94.6vw', overflowX: 'auto' }}
            >
              <div
                className="pb-3 flex-shrink-0"
                onClick={() => setActiveTab('sites')}
              >
                <div
                  style={
                    activeTab === 'sites'
                      ? {
                          cursor: 'pointer',
                          marginRight: '1.5rem',
                          paddingBottom: '1rem',
                          marginBottom: '1rem',
                          borderBottom: '5px #A6478E solid',
                        }
                      : {
                          cursor: 'pointer',
                          marginRight: '1.5rem',
                          paddingBottom: '1rem',
                          marginBottom: '1rem',
                        }
                  }
                >
                  <strong
                    className={
                      activeTab === 'sites' ? 'text-brand' : 'text-muted'
                    }
                  >
                    Sites ({sites.length})
                  </strong>
                </div>
              </div>

              {/* <div
                className="pb-3 flex-shrink-0"
                onClick={() => setActiveTab('users')}
              >
                <div
                  style={
                    activeTab === 'users'
                      ? {
                          cursor: 'pointer',
                          marginRight: '1.5rem',
                          paddingBottom: '1rem',
                          marginBottom: '1rem',
                          borderBottom: '5px #A6478E solid',
                        }
                      : {
                          cursor: 'pointer',
                          marginRight: '1.5rem',
                          paddingBottom: '1rem',
                          marginBottom: '1rem',
                        }
                  }
                >
                  <strong
                    className={
                      activeTab === 'users' ? 'text-brand' : 'text-muted'
                    }
                  >
                    Users
                  </strong>
                </div>
              </div> */}

              <div
                className="pb-3 flex-shrink-0"
                onClick={() => setActiveTab('leaves')}
              >
                <div
                  style={
                    activeTab === 'leaves'
                      ? {
                          cursor: 'pointer',
                          marginRight: '1.5rem',
                          paddingBottom: '1rem',
                          marginBottom: '1rem',
                          borderBottom: '5px #A6478E solid',
                        }
                      : {
                          cursor: 'pointer',
                          marginRight: '1.5rem',
                          paddingBottom: '1rem',
                          marginBottom: '1rem',
                        }
                  }
                >
                  <strong
                    className={
                      activeTab === 'leaves' ? 'text-brand' : 'text-muted'
                    }
                  >
                    Leaves
                  </strong>
                </div>
              </div>
            </div>

            {activeTab === 'sites' && (
              <div className="d-flex flex-column">
                <div className="card bg-white text-dark shadow rounded">
                  <div className="card-body">
                    <div className="border-bottom pb-3 pt-2">
                      <strong>Sites ({sites.length})</strong>
                    </div>
                    <div className="row pb-4">
                      {sites.map((site) => (
                        <div key={site.id} className="col-sm-4 pt-4">
                          <div
                            className="card bg-white text-dark rounded py-4"
                            onClick={() =>
                              history.push(
                                `/clients/client/${client.id}/sites/site/${site.id}`,
                              )
                            }
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="card-body d-flex flex-column justify-content-center align-items-center">
                              <div
                                style={{
                                  height: '6rem',
                                  width: '6rem',
                                  minHeight: '6rem',
                                  minWidth: '6rem',
                                  borderRadius: '50%',
                                  backgroundColor: '#e4e4e4',
                                  paddingTop: '2rem',
                                  paddingLeft: '1.7rem',
                                  lineHeight: '2rem',
                                }}
                              >
                                <i className="fas fa-warehouse fa-2x"></i>
                              </div>
                              <div className="pt-4">
                                <strong>{site.name}</strong>
                              </div>
                              {!site.active && (
                                <div className="pt-2 text-danger">
                                  (Deactivated)
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="col-sm-4 pt-4">
                        <div
                          className="card bg-white text-dark rounded py-4"
                          onClick={() =>
                            history.push(
                              `/clients/client/${client.id}/sites/add`,
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body d-flex flex-column justify-content-center align-items-center">
                            <div
                              style={{
                                height: '6rem',
                                width: '6rem',
                                minHeight: '6rem',
                                minWidth: '6rem',
                                borderRadius: '50%',
                                backgroundColor: '#e4e4e4',
                                paddingTop: '2rem',
                                paddingLeft: '2.1rem',
                                lineHeight: '2rem',
                              }}
                            >
                              <i className="fas fa-plus fa-2x"></i>
                            </div>
                            <div className="pt-4">
                              <strong>Add new site</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'users' && (
              <div className="d-flex flex-column">
                <div className="card bg-white text-dark shadow rounded">
                  <div className="card-body">
                    {activeUserTabPage === 'list' && (
                      <UserManagement
                        clientId={client.id}
                        setActiveUserTabPage={setActiveUserTabPage}
                        setSelectedUser={setSelectedUser}
                      />
                    )}
                    {activeUserTabPage === 'add' && (
                      <AddUserManagement
                        clientId={client.id}
                        setActiveUserTabPage={setActiveUserTabPage}
                        setActiveTab={setActiveTab}
                      />
                    )}
                    {activeUserTabPage === 'edit' && (
                      <EditUserManagement
                        clientId={client.id}
                        setActiveUserTabPage={setActiveUserTabPage}
                        selectedUser={selectedUser}
                        setActiveTab={setActiveTab}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'leaves' && (
              <div className="d-flex flex-column">
                <div className="card bg-white text-dark shadow rounded">
                  <div className="card-body">
                    <div className="border-bottom pb-3 pt-2">
                      <strong>Leave Management</strong>
                    </div>
                    <Card>
                      <CardBody>
                        <Form
                          validated={validated}
                          onSubmit={handleSubmitLeaveSettingsHandler}
                        >
                          <Card className="shadow-sm my-4">
                            <CardHeader>
                              <div className="d-flex justify-content-between align-items-center py-2">
                                <h6 className="mb-0">Approval Flow</h6>
                              </div>
                            </CardHeader>
                            <CardBody>
                              <div className="text-end mb-2">
                                <button
                                  onClick={addLeaveApprover}
                                  className="btn btn-outline-brand ms-3 adjust-fs adjust-shadow"
                                  style={{ width: '200px' }}
                                >
                                  Add Approver
                                </button>
                                <button
                                  type="submit"
                                  className="btn btn-brand ms-3 adjust-fs adjust-shadow"
                                  style={{ width: '12rem' }}
                                >
                                  Save
                                </button>
                              </div>

                              {leaveApprovers && leaveApprovers.length > 0 ? (
                                leaveApprovers.map((approver, index) => (
                                  <div
                                    key={approver.id}
                                    className="mt-2 mb-4 p-3 border rounded"
                                  >
                                    <div className="row align-items-center">
                                      <div className="col-md-3">
                                        <label className="form-label fw-bold mb-0">
                                          Approver {index + 1}
                                        </label>
                                      </div>
                                      <div className="col-md-6">
                                        <select
                                          className="form-select"
                                          value={approver.value}
                                          onChange={(e) =>
                                            handleLeaveApproverChange(
                                              approver.id,
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">
                                            Select an approver
                                          </option>
                                          <option value="client">Client</option>
                                          <option value="admin">Admin</option>
                                        </select>
                                      </div>
                                      <div className="col-md-3">
                                        <button
                                          className="btn p-0 text-danger"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            removeLeaveApprover(approver.id);
                                          }}
                                        >
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div
                                  className="text-center p-4"
                                  style={{ marginTop: '20px' }}
                                >
                                  No Records Found
                                </div>
                              )}
                            </CardBody>
                          </Card>
                        </Form>

                        <Card className="shadow-sm my-4">
                          <CardHeader>
                            <div className="d-flex justify-content-between align-items-center py-2">
                              <h6 className="mb-0">Archive Leaves</h6>
                            </div>
                          </CardHeader>

                          <CardBody>
                            <div className="row align-items-center">
                              {/* Label */}
                              <div className="col-md-3">
                                <label className="form-label fw-bold mb-0">
                                  Select Year
                                </label>
                              </div>

                              {/* Dropdown */}
                              <div className="col-md-4">
                                <select
                                  className="form-select"
                                  value={archiveYear}
                                  onChange={(e) =>
                                    setArchiveYear(e.target.value)
                                  }
                                >
                                  {generateYearOptions().map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="mt-3">
                              <small className="text-muted">
                                This will archive all leave records for the
                                selected year.
                              </small>
                            </div>

                            <div className="text-end mb-2">
                              <button
                                className="btn btn-brand adjust-fs adjust-shadow"
                                style={{ width: '200px' }}
                                onClick={handleArchiveLeaves}
                                disabled={processingArchive || !archiveYear}
                              >
                                {processingArchive ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Processing...
                                  </>
                                ) : (
                                  'Process'
                                )}
                              </button>
                            </div>
                          </CardBody>
                        </Card>

                        <Card className="shadow-sm my-4">
                          <CardHeader>
                            <div className="d-flex justify-content-between align-items-center py-2">
                              <h6 className="mb-0">Carry Forward Leaves</h6>
                            </div>
                          </CardHeader>

                          <CardBody>
                            <div className="row align-items-center">
                              {/* Label */}
                              <div className="col-md-3">
                                <label className="form-label fw-bold mb-0">
                                  Select Year
                                </label>
                              </div>

                              {/* Dropdown */}
                              <div className="col-md-4">
                                <select
                                  className="form-select"
                                  value={carryForwardYear}
                                  onChange={(e) =>
                                    setCarryForwardYear(e.target.value)
                                  }
                                >
                                  {generateYearOptions().map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="mt-3">
                              <small className="text-muted">
                                This will carry forward unused leave credits
                                from the selected year to the next year.
                              </small>
                            </div>
                            <div className="text-end mb-2">
                              <button
                                className="btn btn-brand adjust-fs adjust-shadow"
                                style={{ width: '200px' }}
                                onClick={handleCarryForwardLeaves}
                                disabled={
                                  processingCarryForward || !carryForwardYear
                                }
                              >
                                {processingCarryForward ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Processing...
                                  </>
                                ) : (
                                  'Process'
                                )}
                              </button>
                            </div>
                          </CardBody>
                        </Card>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className="card bg-white text-dark shadow rounded d-flex flex-column"
            style={{
              marginTop: '3rem',
              marginRight: '3rem',
              marginBottom: '3rem',
              width: '17rem',
              minWidth: '17rem',
            }}
          >
            <div className="card-body">
              <div className="container-fluid d-flex flex-column m-2">
                <div className="d-flex flex-column pb-4">
                  <div className="border-bottom pb-3 mb-3">
                    <h5>Client Contact</h5>
                  </div>
                  <div className="pb-2">Name</div>
                  <strong className="pb-2">
                    {client.manager_first_name} {client.manager_last_name}
                  </strong>
                  <div className="pb-2">Email</div>
                  <strong className="pb-2">{client.email}</strong>
                </div>
                <div className="d-flex flex-column pb-4">
                  <div className="border-bottom pb-3 mb-3">
                    <h5>Settings</h5>
                  </div>
                  <div className="pb-2">Smart Scheduler</div>
                  <span
                    className={
                      'badge rounded-pill mb-2 py-2' +
                      (client.enable_smart_scheduler
                        ? ' bg-success'
                        : ' bg-danger')
                    }
                  >
                    {client.enable_smart_scheduler ? 'Enabled' : 'Disabled'}
                  </span>
                  <div className="pb-2">Leave Credits & Filling</div>
                  <span
                    className={
                      'badge rounded-pill mb-2 py-2' +
                      (client.enable_leave_credits
                        ? ' bg-success'
                        : ' bg-danger')
                    }
                  >
                    {client.enable_leave_credits ? 'Enabled' : 'Disabled'}
                  </span>

                  <div className="pb-2">Claims</div>
                  <span
                    className={
                      'badge rounded-pill mb-2 py-2' +
                      (client.enable_claims ? ' bg-success' : ' bg-danger')
                    }
                  >
                    {client.enable_claims ? 'Enabled' : 'Disabled'}
                  </span>

                  <div className="pb-2">Communication</div>
                  <span
                    className={
                      'badge rounded-pill mb-2 py-2' +
                      (client.enable_communication
                        ? ' bg-success'
                        : ' bg-danger')
                    }
                  >
                    {client.enable_communication ? 'Enabled' : 'Disabled'}
                  </span>

                  <div className="pb-2">Events</div>
                  <span
                    className={
                      'badge rounded-pill mb-2 py-2' +
                      (client.enable_event ? ' bg-success' : ' bg-danger')
                    }
                  >
                    {client.enable_event ? 'Enabled' : 'Disabled'}
                  </span>
                  <div className="pb-2">Start day of the week</div>
                  <span className="badge rounded-pill bg-secondary mb-2 py-2">
                    {client.start_day === 0
                      ? 'Sun-Sat'
                      : client.start_day === 1
                        ? 'Mon-Sun'
                        : 'Sat-Fri'}
                  </span>
                </div>
                {client && client.contacts && client.contacts.length > 0 && (
                  <div className="d-flex flex-column">
                    <div className="border-bottom pb-3 mb-3">
                      <h5>Escalation Contacts</h5>
                    </div>
                    {client.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="container py-1 d-flex flex-column"
                        style={{ backgroundColor: '##fafafa' }}
                      >
                        <div className="text-muted">Name</div>
                        <strong className="pb-2">
                          {contact.first_name} {contact.last_name}
                        </strong>
                        <div className="text-muted">SEV Level</div>
                        <strong className="pb-2">
                          {contact.severity.name}
                        </strong>
                        <div className="text-muted">Email</div>
                        <strong className="pb-2">{contact.email}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
